/**
 * BudgetChatPanel — mini chat thread between admin and school tied to
 * a single budget.  Both sides use the same component (passing
 * different `role` + API prefix) so a message written by the admin
 * appears instantly for the school and vice-versa, in chronological
 * order, with a sender badge on each bubble.
 *
 * Props:
 *   budgetId  : string, the orçamento id
 *   role      : "admin" | "school" — controls the API prefix and
 *               bubble alignment ("me" bubbles align right).
 *   onSent    : optional callback fired after a successful post.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, MessageSquare, User, Shield, Check, CheckCheck } from "lucide-react";

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
};

export default function BudgetChatPanel({ budgetId, role = "admin", onSent }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [text, setText]         = useState("");
  const [sending, setSending]   = useState(false);
  const bottomRef = useRef(null);

  const prefix = role === "school" ? "/school" : "";
  const listUrl = `${prefix}/budgets/${budgetId}/messages`;
  const sendUrl = role === "school"
    ? `/school/budgets/${budgetId}/message`
    : `/budgets/${budgetId}/message-school`;

  const load = useCallback(async () => {
    try {
      const r = await api.get(listUrl);
      setMessages(Array.isArray(r.data) ? r.data : []);
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [listUrl]);

  useEffect(() => { load(); }, [load]);

  // Keep the view pinned to the latest message.  We scroll in two
  // passes so new messages that arrive via `load()` right after a send
  // are visible even when the DOM hasn't committed the last paint yet:
  //   • first on the current tick (handles the optimistic case),
  //   • again on the next animation frame (handles the fetched case).
  const scrollToBottom = useCallback(() => {
    const el = bottomRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "end" });
    requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "end" }));
  }, []);
  useEffect(() => { scrollToBottom(); }, [messages.length, scrollToBottom]);

  // Light polling so each side sees the other's answer without a reload.
  // Cheap because /messages returns in <50ms for a typical budget.
  useEffect(() => {
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [load]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      // Admin side always notifies the school; silencing the budget
      // page toggle is only for status/item edits — explicit messages
      // are intentional communication.
      await api.post(sendUrl, { text: t }, { headers: { "X-Notify-School": "1" } });
      setText("");
      await load();
      scrollToBottom();
      onSent?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar mensagem");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 card-shadow p-4"
      data-testid="budget-chat-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-sky-600" />
        <h2 className="font-heading text-base font-black text-slate-900">
          Conversa com {role === "school" ? "a AMC" : "a escola"}
        </h2>
        {messages.length > 0 && (
          <span className="text-[10px] font-black bg-sky-100 text-sky-800 rounded-full px-2 py-0.5 ml-auto">
            {messages.length}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      ) : messages.length === 0 ? (
        <p className="text-sm text-slate-400 italic text-center py-4">
          Nenhuma mensagem ainda. Comece a conversa.
        </p>
      ) : (
        <ul className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="chat-list">
          {messages.map((m) => {
            const mine = m.sender === role;
            const SenderIcon = m.sender === "admin" ? Shield : User;
            return (
              <li
                key={m.id}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${m.id}`}
              >
                <div className={`max-w-[82%] rounded-2xl px-3 py-2 ${
                  mine
                    ? "bg-[#1e3a8a] text-white rounded-br-sm"
                    : "bg-slate-100 text-slate-900 rounded-bl-sm"
                }`}>
                  <div className={`flex items-center gap-1 text-[10px] font-bold mb-0.5 ${
                    mine ? "text-sky-200" : "text-slate-500"
                  }`}>
                    <SenderIcon className="w-2.5 h-2.5" />
                    {m.sender === "admin" ? "AMC" : "Escola"}{m.sender_name ? ` — ${m.sender_name}` : ""}
                    <span className="opacity-70">· {formatTime(m.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-wrap text-[13px] leading-snug">{m.text}</p>
                  {/* Read receipt — only on messages I sent: ✓ delivered,
                      ✓✓ (teal) when the other side opened the thread. */}
                  {mine && (
                    <div className="flex justify-end mt-0.5 h-3" aria-hidden="true">
                      {m.read_at
                        ? <CheckCheck className="w-3 h-3 text-sky-300" data-testid={`chat-read-${m.id}`} />
                        : <Check className="w-3 h-3 text-sky-200/70" data-testid={`chat-sent-${m.id}`} />
                      }
                    </div>
                  )}
                </div>
              </li>
            );
          })}
          <li ref={bottomRef} />
        </ul>
      )}

      <form onSubmit={submit} className="mt-3 flex items-start gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={role === "school" ? "Escreva uma mensagem para a AMC..." : "Escreva uma mensagem para a escola..."}
          rows={2}
          maxLength={2000}
          className="flex-1 min-h-[2.5rem] text-sm"
          data-testid="chat-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); }
          }}
        />
        <Button
          type="submit"
          disabled={sending || !text.trim()}
          className="bg-sky-600 hover:bg-sky-700 text-white h-[2.5rem]"
          data-testid="chat-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </section>
  );
}
